import { FC, useCallback, useEffect, useState } from 'react';

import { PageHead } from 'views/design-system/components';

import { useDataFeeds } from 'views/transaction-monitoring/data/use-tm';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import 'views/transaction-monitoring/txn-surface.scss';
import './data-pulls-deck.scss';

/**
 * DATA PULLS — the health of everything we collect on a schedule.
 *
 * INTERNAL ONLY, and outside Transaction Monitoring on purpose. The pulls this
 * reports on are not all TM's: aml.api refreshes sanctions nightly, clients.api
 * bills monthly. Burying a platform-wide view inside one product's sub-menu
 * would have made the others look like TM's business.
 *
 * Named for the one feed that exists. The storage underneath is keyed by
 * `feedKey` and the label comes from the record rather than this file, so the
 * second feed is a row and the eventual rename to "Data feeds" is a data change,
 * not a redesign.
 *
 * WHAT THIS PAGE IS FOR, stated because it shapes every choice below: answering
 * "did last night work, and can I trust what it collected" WITHOUT reading logs.
 * So a failed run is rendered as carefully as a successful one, and the reason is
 * the sentence the collector actually recorded rather than a status word.
 */

const fmtBytes = (n: number): string =>
	n >= 1e6
		? `${(n / 1e6).toFixed(1)} MB`
		: n >= 1e3
		? `${(n / 1e3).toFixed(0)} KB`
		: `${n} B`;

const fmtWhen = (iso?: string | null): string =>
	iso ? new Date(iso).toLocaleString() : '—';

const fmtDur = (a?: string | null, b?: string | null): string => {
	if (!a || !b) return '—';
	const ms = new Date(b).getTime() - new Date(a).getTime();
	return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)}s`;
};

export const DataPullsDeck: FC = () => {
	const {
		feed,
		health,
		runs,
		recipients,
		scheduler,
		pulls,
		coverage,
		alerting,
		loading,
		failed,
		reload,
		runNow,
		testNotify,
		addRecipient,
		removeRecipient,
	} = useDataFeeds();
	const [busy, setBusy] = useState<string | null>(null);
	const [email, setEmail] = useState('');
	const [notice, setNotice] = useState<string | null>(null);
	const [notifyResult, setNotifyResult] = useState<any>(null);

	useEffect(() => {
		reload();
	}, [reload]);

	const onRun = useCallback(async () => {
		setBusy('run');
		setNotice(null);
		/* A real collection is a 28MB fetch — around 20 seconds. The button says so
		   rather than appearing hung, because the honest alternative (returning
		   immediately and reporting nothing) is what makes a "run now" button
		   untrustworthy. */
		const res = await runNow();
		setNotice(
			res?.ok
				? `Collected ${res.addressesUnique?.toLocaleString()} addresses${
						res.flags?.length ? ` — ${res.flags.join(' · ')}` : ''
				  }`
				: `The run failed: ${res?.failureReason ?? 'no reason recorded'}`
		);
		setBusy(null);
	}, [runNow]);

	const onTest = useCallback(async () => {
		setBusy('test');
		setNotifyResult(null);
		setNotifyResult(await testNotify());
		setBusy(null);
	}, [testNotify]);

	const onAdd = useCallback(async () => {
		const e = email.trim();
		if (!e) return;
		setBusy('add');
		const res = await addRecipient(e);
		setNotice(res?.error ?? null);
		if (!res?.error) setEmail('');
		setBusy(null);
	}, [email, addRecipient]);

	const state = health?.state ?? 'red';

	return (
		<div className="sv2-scope sv2 txn-deck feeds-deck">
			<PageHead
				/* STATIC. It fell back to `feed?.label` from when this page WAS one feed;
				   now that the registry populates that label the header read "OFAC
				   crypto wallets" above a table of seven pulls. The per-pull label
				   belongs in the detail heading below, not in the page title. */
				title="Data Pulls"
				sub="Scheduled collections and their health — whether last night worked, and whether what arrived can be trusted."
			/>

			{/* THE COVERAGE MATRIX — claimed versus actually collected.
			    First on the page because it is the finding: three sanctions
			    sources are declared in aml.api and screen nothing, and until this
			    existed the only evidence was a log line nobody read. A page that
			    opened with a green feed would have buried it. */}
			{coverage ? (
				<div className={`cov ${coverage.notImplemented ? 'bad' : ''}`}>
					<div className="covn">
						<b>{coverage.collecting}</b> of <b>{coverage.declared}</b> sanctions
						sources actually collecting
					</div>
					{coverage.notImplemented ? (
						<div className="covm">
							<b>Not screened:</b> {coverage.missing.join(' · ')} — declared as
							sources but never built. Anyone told we screen these lists has
							been told something untrue.
						</div>
					) : null}
					{coverage.unobserved ? (
						<div className="covu">
							{coverage.unobserved} source
							{coverage.unobserved === 1 ? '' : 's'} run in another service and
							do not report here yet, so this page cannot vouch for them either
							way.
						</div>
					) : null}
				</div>
			) : null}

			{/* EVERY SCHEDULED PULL, worst first. */}
			<div className="panel">
				<div className="ph">
					Scheduled pulls <span className="r">{pulls.length}</span>
				</div>
				<table className="tbl pulltbl">
					<thead>
						<tr>
							<th>Pull</th>
							<th>Owner</th>
							<th>State</th>
							<th className="num">Records</th>
							<th>Cadence</th>
						</tr>
					</thead>
					<tbody>
						{!pulls.length && (
							<tr>
								<td colSpan={5} className="empty">
									{loading ? 'Loading…' : 'No pulls registered.'}
								</td>
							</tr>
						)}
						{pulls.map((p: any) => (
							<tr key={p.feedKey}>
								<td>
									{p.label}
									{p.sourceLabel ? (
										<div className="rnote gy">{p.sourceLabel}</div>
									) : null}
								</td>
								<td className="mono">{p.owner}</td>
								<td>
									{/* FOUR STATES, not three. "not built" and "we cannot see it"
									    are different from healthy, and neither is a failure a
									    retry would fix — colouring them red would send somebody
									    hunting a broken job that does not exist. */}
									<span
										className={`nd ${
											p.health.state === 'green'
												? 'pass'
												: p.health.state === 'amber'
												? 'review'
												: p.health.state === 'red'
												? 'fail'
												: p.health.state === 'not_implemented'
												? 'locked'
												: 'test'
										}`}
									>
										{p.health.state === 'not_implemented'
											? 'not built'
											: p.health.state === 'unobserved'
											? 'not reporting'
											: p.health.state}
									</span>
									<div className="rnote">{p.health.reason}</div>
								</td>
								{/* THE COUNT IS WHAT MAKES "green" MEAN ANYTHING. A green row with no
								    number says a job finished; it does not say the list it produced is
								    real. "green · 1,011" does, and a list that quietly halves shows up
								    here before any threshold notices. An em dash for a pull that has
								    never reported, never a 0: zero records is a catastrophic finding
								    and must not be how the page renders "we have not heard from this
								    one". */}
								<td className="num mono">
									{p.lastRun?.addressesUnique
										? Number(p.lastRun.addressesUnique).toLocaleString()
										: '—'}
								</td>
								<td className="mono">{p.cadenceCron}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* THE HEADLINE IS THE HEALTH, not the newest run. A feed whose last run
			    succeeded three days ago is not healthy, and nothing about that run
			    says so — which is why the state is computed rather than read off the
			    last row. */}
			<div className="dhead">OFAC crypto wallets — detail</div>
			<div className={`hero hero-${state}`}>
				<div className="hl">
					<span className={`dot ${state}`} />
					{state === 'green'
						? 'Healthy'
						: state === 'amber'
						? 'Needs a look'
						: 'Failing'}
				</div>
				<div className="hr">{health?.reason ?? '—'}</div>
				<div className="hm">
					<span>
						Last good collection <b>{fmtWhen(health?.lastGoodAt)}</b>
					</span>
					<span>
						Next scheduled{' '}
						<b>
							{scheduler?.installed ? fmtWhen(scheduler.next) : 'not scheduled'}
						</b>
						{scheduler?.cron ? (
							<span className="gy"> · {scheduler.cron}</span>
						) : null}
					</span>
					{health && health.consecutiveFailures > 0 ? (
						<span className="r">
							{health.consecutiveFailures} consecutive failure
							{health.consecutiveFailures === 1 ? '' : 's'}
						</span>
					) : null}
				</div>
			</div>

			{/* A SCHEDULER THAT NEVER INSTALLED is the failure this page exists to
			    catch, so it is called out rather than left implicit in a blank
			    "next run" field. */}
			{scheduler && !scheduler.installed ? (
				<div className="warn">
					<b>No nightly schedule is installed.</b> Collection will only happen
					when someone presses Collect now, so this data will go stale without
					anyone being told.
				</div>
			) : null}

			<div className="acts">
				<button
					className="pre on"
					type="button"
					disabled={!!busy}
					onClick={onRun}
				>
					{busy === 'run' ? 'Collecting… (~20s)' : 'Collect now'}
				</button>
				<button
					className="pre"
					type="button"
					disabled={!!busy}
					onClick={onTest}
				>
					{busy === 'test' ? 'Testing…' : 'Test the alert list'}
				</button>
				{notice ? <span className="note">{notice}</span> : null}
			</div>

			{/* WHETHER MAIL ACTUALLY SENT, per recipient — not whether we tried.
			    This stack's existing senders swallow their errors, so "attempted"
			    would be a claim the operator could not rely on. */}
			{notifyResult ? (
				<div className={`warn ${notifyResult.canNotify ? '' : 'bad'}`}>
					<b>
						{notifyResult.canNotify
							? 'Alert path reachable.'
							: 'Cannot send alerts.'}
					</b>{' '}
					{notifyResult.reason}
					{notifyResult.recipients?.length ? (
						<ul>
							{notifyResult.recipients.map((r: any) => (
								<li key={r.email}>
									{r.email} — {r.sent ? 'sent' : `not sent (${r.error})`}
								</li>
							))}
						</ul>
					) : null}
				</div>
			) : null}

			<div className="panel">
				<div className="ph">
					Recent collections <span className="r">{runs.length}</span>
				</div>
				<table className="tbl runtbl">
					<thead>
						<tr>
							<th>Started</th>
							<th>Result</th>
							<th className="num">Addresses</th>
							<th className="num">Change</th>
							<th className="num">Size</th>
							<th className="num">Took</th>
							<th>Source published</th>
						</tr>
					</thead>
					<tbody>
						{!runs.length && (
							<tr>
								<td colSpan={7} className="empty">
									{loading
										? 'Loading…'
										: failed
										? 'Could not reach transaction monitoring.'
										: 'This feed has never run.'}
								</td>
							</tr>
						)}
						{runs.map((r: any) => (
							<tr key={r._id}>
								<td className="mono">{fmtWhen(r.startedAt)}</td>
								<td>
									<span
										className={`nd ${
											r.status === 'succeeded'
												? 'pass'
												: r.status === 'flagged'
												? 'review'
												: r.status === 'failed'
												? 'fail'
												: ''
										}`}
									>
										{r.status}
									</span>
									{/* The recorded sentence, verbatim. A status word alone would
									    make an operator open the logs, which is the thing this
									    page exists to avoid. */}
									{r.failureReason ? (
										<div className="rnote">{r.failureReason}</div>
									) : null}
									{r.httpChain?.length ? (
										<div className="rnote gy">
											HTTP {r.httpChain.map((h: any) => h.status).join(' → ')}
										</div>
									) : null}
								</td>
								<td className="num mono">
									{r.addressesUnique ? r.addressesUnique.toLocaleString() : '—'}
								</td>
								<td className="num mono">
									{r.deltaVsPriorGoodRun === null ||
									r.deltaVsPriorGoodRun === undefined
										? '—'
										: `${r.deltaVsPriorGoodRun > 0 ? '+' : ''}${
												r.deltaVsPriorGoodRun
										  }%`}
								</td>
								<td className="num mono">
									{r.bytesReceived ? fmtBytes(r.bytesReceived) : '—'}
								</td>
								<td className="num mono">
									{fmtDur(r.startedAt, r.finishedAt)}
								</td>
								<td className="mono">
									{r.sourcePublishDate
										? String(r.sourcePublishDate).slice(0, 10)
										: '—'}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className="panel alertpanel">
				<div className="ph">
					Alert recipients <span className="r">{recipients.length}</span>
				</div>
				{/* THE STATE OF THE NOTIFIER ITSELF, not of the feed.
				    A green feed watched by an alerter that reaches nobody is not a
				    monitored feed, and that is only knowable BEFORE the bad night if the
				    page says so. `Test the alert list` proves the path on demand; this
				    line reports it without being asked. */}
				{alerting ? (
					<div className={`warn ${alerting.canNotify ? 'ok' : 'bad'}`}>
						<b>
							{alerting.canNotify
								? 'Automatic alerts are armed.'
								: 'Automatic alerts would not be delivered.'}
						</b>{' '}
						{alerting.note}
						{alerting.notifiedAt ? (
							<div className="rnote">
								Last sent {fmtWhen(alerting.notifiedAt)} — reported this pull as{' '}
								<b>{alerting.notifiedState}</b>
								{alerting.notifiedReason ? ` (${alerting.notifiedReason})` : ''}
								.
							</div>
						) : null}
						{alerting.lastOutcome?.recipients?.length ? (
							<ul>
								{alerting.lastOutcome.recipients.map((r: any) => (
									<li key={r.email}>
										{r.email} — {r.sent ? 'sent' : `not sent (${r.error})`}
									</li>
								))}
							</ul>
						) : null}
					</div>
				) : null}

				<div className="reciplist">
					{!recipients.length ? (
						<div className="empty">No addresses yet — add one below.</div>
					) : (
						recipients.map((r: any) => (
							<span className="chip" key={`${r.email}-${r.feedKey ?? 'all'}`}>
								{r.email}
								<button
									type="button"
									aria-label={`Remove ${r.email}`}
									disabled={!!busy}
									onClick={async () => {
										setBusy('rm');
										await removeRecipient(r.email);
										setBusy(null);
									}}
								>
									×
								</button>
							</span>
						))
					)}
				</div>
				<div className="addrow">
					<input
						type="email"
						placeholder="name@company.com"
						value={email}
						onChange={e => setEmail(e.target.value)}
						onKeyDown={e => e.key === 'Enter' && onAdd()}
						aria-label="Email address to notify"
					/>
					<button
						className="pre on"
						type="button"
						disabled={!!busy || !email.trim()}
						onClick={onAdd}
					>
						Add
					</button>
				</div>
				<div className="foot">
					Recipients are told when a pull CHANGES state — starts failing, or
					recovers — not on every imperfect run. A pull down for a week sends
					<b> one</b> message, not seven: the same red repeated nightly does not
					inform anyone better, it teaches people to filter the sender. Health
					is re-checked <b>every hour</b>, which is what catches a job that
					stopped running at all rather than one that failed loudly. Only mail
					that actually left the building counts as told — if delivery fails,
					the change stays outstanding and is retried. Use{' '}
					<b>Test the alert list</b> to prove the path before you need it.
				</div>
			</div>
		</div>
	);
};

export default DataPullsDeck;
